import React, { useState } from 'react';
import {
  Card, Typography, Tag, Button, Upload, Steps, Alert, Input,
  Descriptions, Tooltip, Space, Divider, Progress, Spin, message,
} from 'antd';
import {
  InboxOutlined, FilePdfOutlined, DeleteOutlined,
  CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined,
  EditOutlined, CheckOutlined, CloseOutlined, RedoOutlined,
  InfoCircleOutlined, WarningOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Dragger } = Upload;
const { Title, Text } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const DOC_TYPE_LABELS = {
  invoice: 'Invoice', delivery_note: 'Delivery Note', timesheet: 'Timesheet',
  ipc: 'Interim Payment Certificate (IPC)', job_delivery_report: 'Job Delivery Notes/Report',
  handover_report: 'Hand-over Report', attendance_sheet: 'Attendance Sheet',
  summary_of_attendance: 'Summary of Attendance', job_completion: 'Job Completion Report',
  other: 'Other / Supporting Document',
};

const formatAmount = (val) => {
  if (!val && val !== 0) return '-';
  return `AED ${Number(val).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

const getQualityColor = (score) => {
  if (score >= 85) return '#52c41a';
  if (score >= 60) return '#d48806';
  return '#cf1322';
};

/**
 * RevisionUploadForm — SINGLE PDF flow (same as initial submission)
 * Upload one PDF → Analyze (classify pages + extract) → Review → Submit revision
 */
const RevisionUploadForm = ({ submissionId, submissionRef, category, rejectionRemarks, username, onComplete }) => {
  const [revStep, setRevStep] = useState('upload'); // upload → analyzing → review → submitting → done
  const [pdfFile, setPdfFile] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisError, setAnalysisError] = useState('');
  const [invoiceData, setInvoiceData] = useState({});
  const [metadata, setMetadata] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [analyzeStep, setAnalyzeStep] = useState(0);
  const [submitStep, setSubmitStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Editing helpers
  const startEdit = (field, val) => { setEditingField(field); setEditValue(val || ''); };
  const saveEdit = (field, isMeta = false) => {
    if (isMeta) setMetadata(prev => ({ ...prev, [field]: editValue }));
    else setInvoiceData(prev => ({ ...prev, [field]: editValue }));
    setEditingField(null); setEditValue('');
  };
  const cancelEdit = () => { setEditingField(null); setEditValue(''); };

  /* ── amount editing with auto-recalculation (UAE VAT 5%) ── */
  const parseNum = (v) => {
    const n = parseFloat(String(v ?? '').replace(/[^\d.\-]/g, ''));
    return isNaN(n) ? null : n;
  };

  const saveAmountEdit = (field) => {
    const entered = parseNum(editValue);
    if (entered === null || entered < 0) { message.error('Enter a valid amount'); return; }

    let subtotal = parseNum(invoiceData.subtotal_amount) || 0;
    let vat      = parseNum(invoiceData.vat_amount) || 0;
    let total    = parseNum(invoiceData.total_amount) || 0;

    if (field === 'subtotal_amount') {
      subtotal = entered;
      vat   = Math.round(subtotal * 0.05 * 100) / 100;
      total = Math.round((subtotal + vat) * 100) / 100;
    } else if (field === 'vat_amount') {
      vat   = entered;                                   // keep subtotal, recompute total
      total = Math.round((subtotal + vat) * 100) / 100;
    } else { // total_amount
      total    = entered;                                // derive subtotal + VAT from total
      subtotal = Math.round((total / 1.05) * 100) / 100;
      vat      = Math.round((total - subtotal) * 100) / 100;
    }

    setInvoiceData(prev => ({
      ...prev,
      subtotal_amount: subtotal.toFixed(2),
      vat_amount: vat.toFixed(2),
      total_amount: total.toFixed(2),
    }));
    setEditingField(null); setEditValue('');
  };

  const renderEditableAmount = (field, value, opts = {}) => {
    if (editingField === field) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Input size="small" prefix="AED" value={editValue} onChange={e => setEditValue(e.target.value)}
            onPressEnter={() => saveAmountEdit(field)} style={{ borderRadius: 6, maxWidth: 200 }} autoFocus />
          <Button type="text" size="small" icon={<CheckOutlined />} onClick={() => saveAmountEdit(field)} style={{ color: '#52c41a' }} />
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={cancelEdit} style={{ color: '#ff4d4f' }} />
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Text strong={!!opts.strong} style={{ fontSize: opts.strong ? 15 : 13, color: value ? '#1a3353' : '#bfbfbf' }}>
          {value ? formatAmount(value) : 'Not detected'}
        </Text>
        <Tooltip title="Edit">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEdit(field, value)}
            style={{ color: '#8c8c8c', padding: '0 4px', fontSize: 12, opacity: 0 }} className="rev-edit-btn" />
        </Tooltip>
      </div>
    );
  };

  const renderEditable = (field, value, isMeta = false) => {
    if (editingField === field) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Input size="small" value={editValue} onChange={e => setEditValue(e.target.value)}
            onPressEnter={() => saveEdit(field, isMeta)} style={{ borderRadius: 6, flex: 1, maxWidth: 280 }} autoFocus />
          <Button type="text" size="small" icon={<CheckOutlined />} onClick={() => saveEdit(field, isMeta)} style={{ color: '#52c41a' }} />
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={cancelEdit} style={{ color: '#ff4d4f' }} />
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: value ? '#1a3353' : '#bfbfbf' }}>{value || 'Not detected'}</span>
        <Tooltip title="Edit"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEdit(field, value)}
          style={{ color: '#8c8c8c', padding: '0 4px', fontSize: 12, opacity: 0 }} className="rev-edit-btn" /></Tooltip>
      </div>
    );
  };

  // ── ANALYZE (same as initial submission flow) ──
  const handleAnalyze = async () => {
    if (!pdfFile) return;
    setRevStep('analyzing');
    setAnalyzeStep(0);
    setAnalysisError('');

    const formData = new FormData();
    formData.append('pdf_file', pdfFile);
    formData.append('category', category);

    const t1 = setTimeout(() => setAnalyzeStep(1), 2000);
    const t2 = setTimeout(() => setAnalyzeStep(2), 5000);
    const t3 = setTimeout(() => setAnalyzeStep(3), 8000);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/procurement/analyze-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000,
      });
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      setAnalysisResult(res.data);
      setInvoiceData(res.data.invoice_data || {});
      setMetadata(res.data.metadata || {});
      setRevStep('review');
      if (!res.data.success) setAnalysisError(res.data.message || 'Analysis had issues');
    } catch (err) {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      setAnalysisError(err.response?.data?.message || 'Analysis failed');
      setRevStep('upload');
    }
  };

  // ── SUBMIT REVISION (single PDF, same as initial) ──
  const handleSubmitRevision = async () => {
    setSubmitting(true);
    setRevStep('submitting');
    setSubmitStep(0);
    setError('');

    const formData = new FormData();
    formData.append('pdf_file', pdfFile);
    formData.append('category', category);
    formData.append('submitted_by', username);
    formData.append('invoice_data_json', JSON.stringify(invoiceData));
    formData.append('metadata_json', JSON.stringify(metadata));
    formData.append('detected_doc_types_json', JSON.stringify(analysisResult?.detected_doc_types || []));

    const t1 = setTimeout(() => setSubmitStep(1), 2000);
    const t2 = setTimeout(() => setSubmitStep(2), 4000);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/procurement/submissions/${submissionId}/revise`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000,
      });
      clearTimeout(t1); clearTimeout(t2);
      setSubmitStep(3);
      if (res.data.success) {
        message.success(`Revision #${res.data.revision_number} submitted for ${submissionRef}`);
        setRevStep('done');
        if (onComplete) onComplete();
      } else {
        setError(res.data.message || 'Revision failed');
        setRevStep('review');
      }
    } catch (err) {
      clearTimeout(t1); clearTimeout(t2);
      setError(err.response?.data?.message || 'Revision failed');
      setRevStep('review');
    } finally {
      setSubmitting(false);
    }
  };

  // ── UPLOAD STEP ──
  if (revStep === 'upload') {
    return (
      <div>
        <style>{`.rev-edit-btn { opacity: 0 !important; transition: opacity 0.15s; } .ant-descriptions-item-content:hover .rev-edit-btn { opacity: 1 !important; }`}</style>

        {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 12, borderRadius: 8 }} />}

        <Text strong style={{ fontSize: 13, color: '#1a3353', display: 'block', marginBottom: 8 }}>
          Upload revised document (single PDF with all required documents)
        </Text>
        <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 12 }}>
          Same as initial submission — upload one PDF containing invoice and all supporting documents. AI will classify pages and extract data.
        </Text>

        {pdfFile ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: 8, marginBottom: 16 }}>
            <FilePdfOutlined style={{ color: '#cf1322', fontSize: 28 }} />
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 14, color: '#1a3353', display: 'block' }}>{pdfFile.name}</Text>
              <Text style={{ fontSize: 12, color: '#8c8c8c' }}>{formatFileSize(pdfFile.size)}</Text>
            </div>
            <Tag color="success" style={{ borderRadius: 4 }}>Ready</Tag>
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => setPdfFile(null)} />
          </div>
        ) : (
          <Dragger showUploadList={false}
            beforeUpload={(f) => { if (f.size > 100 * 1024 * 1024) { message.error('File too large. Max 100 MB.'); return Upload.LIST_IGNORE; } setPdfFile(f); return false; }}
            accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
            style={{ borderRadius: 10, borderColor: '#722ed1', borderWidth: 2, marginBottom: 16, padding: '16px 0' }}>
            <p style={{ marginBottom: 8 }}><InboxOutlined style={{ fontSize: 36, color: '#722ed1' }} /></p>
            <p style={{ fontSize: 14, color: '#1a3353', fontWeight: 600, marginBottom: 4 }}>Drop revised document here</p>
            <p style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>Single PDF with invoice + all supporting docs</p>
            <p style={{ color: '#bfbfbf', fontSize: 11 }}>PDF, JPG, PNG accepted. Max 100 MB.</p>
          </Dragger>
        )}

        <Button type="primary" size="large" block onClick={handleAnalyze} disabled={!pdfFile}
          style={{ borderRadius: 8, height: 46, fontWeight: 600, background: '#722ed1', borderColor: '#722ed1' }}>
          Analyze Revised Document
        </Button>
      </div>
    );
  }

  // ── ANALYZING STEP ──
  if (revStep === 'analyzing') {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <LoadingOutlined style={{ fontSize: 36, color: '#722ed1' }} spin />
        <Title level={5} style={{ color: '#1a3353', margin: '16px 0 8px' }}>Processing Revised Document</Title>
        <Text style={{ color: '#8c8c8c', fontSize: 13, display: 'block', marginBottom: 20 }}>Reading pages, classifying documents, extracting data</Text>
        <div style={{ maxWidth: 350, margin: '0 auto' }}>
          <Steps direction="vertical" size="small" current={analyzeStep} items={[
            { title: 'Uploading document' },
            { title: 'Reading & scanning pages' },
            { title: 'Classifying document types' },
            { title: 'Extracting invoice data' },
          ]} />
        </div>
        <Text style={{ fontSize: 12, color: '#bfbfbf', marginTop: 16, display: 'block' }}>Usually takes 15-30 seconds</Text>
      </div>
    );
  }

  // ── REVIEW STEP ──
  if (revStep === 'review' && analysisResult) {
    const detectedTypes = analysisResult.detected_doc_types || [];
    const allPresent = analysisResult.all_required_present;
    const qualityScore = analysisResult.quality_score || 0;
    const pages = analysisResult.pages || [];

    return (
      <div>
        <style>{`.rev-edit-btn { opacity: 0 !important; transition: opacity 0.15s; } .ant-descriptions-item-content:hover .rev-edit-btn { opacity: 1 !important; }`}</style>

        {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 12, borderRadius: 8 }} />}
        {analysisError && <Alert message={analysisError} type="warning" showIcon style={{ marginBottom: 12, borderRadius: 8 }} />}

        {/* Quality + Document Detection */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <Progress type="circle" percent={qualityScore} width={40} strokeColor={getQualityColor(qualityScore)}
            format={(p) => <span style={{ fontSize: 11, fontWeight: 700, color: getQualityColor(qualityScore) }}>{p}</span>} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {detectedTypes.map(dt => (
                <Tag key={dt} icon={<CheckCircleOutlined />} color="success" style={{ borderRadius: 4, fontSize: 11 }}>
                  {DOC_TYPE_LABELS[dt] || dt}
                </Tag>
              ))}
            </div>
            {!allPresent && (
              <Text style={{ fontSize: 12, color: '#cf1322', display: 'block', marginTop: 4 }}>
                Missing: {(analysisResult.missing_doc_labels || []).join(', ')}
              </Text>
            )}
          </div>
        </div>

        {pages.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>Page classification:</Text>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {pages.map((p, idx) => (
                <Tag key={idx} style={{ borderRadius: 4, fontSize: 10, margin: 0, background: '#fff', border: '1px solid #d9d9d9' }}>
                  Pg {p.page_number}: {DOC_TYPE_LABELS[p.doc_type] || p.doc_type}
                </Tag>
              ))}
            </div>
          </div>
        )}

        {/* Amount verification note */}
        <Alert
          type="warning" showIcon
          message="Check the invoice amounts before re-submitting"
          description="AI-extracted figures aren't always correct. Verify Subtotal, VAT and Total against the revised invoice and click the edit icon to fix any wrong value. Editing one amount auto-recalculates the other two (UAE VAT = 5%). If the invoice has VAT-exempt items, edit the VAT field last to override."
          style={{ marginBottom: 12, borderRadius: 8 }}
        />

        {/* Extracted data */}
        <Descriptions bordered size="small" column={1}
          labelStyle={{ background: '#fafafa', fontWeight: 600, width: 150, color: '#595959', fontSize: 12 }}
          contentStyle={{ fontSize: 13, background: '#fff' }}>
          <Descriptions.Item label="Invoice Number">{renderEditable('invoice_number', invoiceData.invoice_number)}</Descriptions.Item>
          <Descriptions.Item label="Vendor Name">{renderEditable('vendor_name', invoiceData.vendor_name)}</Descriptions.Item>
          <Descriptions.Item label="Invoice Date">{renderEditable('invoice_date', invoiceData.invoice_date)}</Descriptions.Item>
          <Descriptions.Item label="Description">{renderEditable('description', invoiceData.description)}</Descriptions.Item>
          <Descriptions.Item label="Subtotal">{renderEditableAmount('subtotal_amount', invoiceData.subtotal_amount)}</Descriptions.Item>
          <Descriptions.Item label="VAT (5%)">{renderEditableAmount('vat_amount', invoiceData.vat_amount)}</Descriptions.Item>
          <Descriptions.Item label="Total Amount">{renderEditableAmount('total_amount', invoiceData.total_amount, { strong: true })}</Descriptions.Item>
          <Descriptions.Item label="Vendor TRN">{renderEditable('vendor_trn', invoiceData.vendor_trn)}</Descriptions.Item>
          <Descriptions.Item label="Vendor Email">{renderEditable('vendor_email', invoiceData.vendor_email)}</Descriptions.Item>
          <Descriptions.Item label="LPO Reference">{renderEditable('lpo_reference', invoiceData.lpo_reference)}</Descriptions.Item>
        </Descriptions>

        {/* Metadata */}
        {Object.values(metadata || {}).some(v => v) && (
          <div style={{ marginTop: 12 }}>
            <Descriptions bordered size="small" column={1}
              labelStyle={{ background: '#fafafa', fontWeight: 600, width: 150, color: '#595959', fontSize: 12 }}
              contentStyle={{ fontSize: 13, background: '#fff' }}>
              {metadata.delivery_note_number != null && <Descriptions.Item label="DN Number">{renderEditable('delivery_note_number', metadata.delivery_note_number, true)}</Descriptions.Item>}
              {metadata.delivery_note_date != null && <Descriptions.Item label="DN Date">{renderEditable('delivery_note_date', metadata.delivery_note_date, true)}</Descriptions.Item>}
              {metadata.received_by != null && <Descriptions.Item label="Received By">{renderEditable('received_by', metadata.received_by, true)}</Descriptions.Item>}
            </Descriptions>
          </div>
        )}

        <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
          <Button onClick={() => { setRevStep('upload'); setAnalysisResult(null); setPdfFile(null); }} style={{ borderRadius: 8 }}>Re-upload</Button>
          <Button type="primary" size="large" onClick={handleSubmitRevision} disabled={!allPresent}
            style={{ borderRadius: 8, height: 44, fontWeight: 600, flex: 1, background: '#722ed1', borderColor: '#722ed1' }}>
            {allPresent ? 'Submit Revised Invoice' : `Cannot Submit — Missing: ${(analysisResult.missing_doc_labels || []).join(', ')}`}
          </Button>
        </div>
      </div>
    );
  }

  // ── SUBMITTING ──
  if (revStep === 'submitting') {
    return (
      <div style={{ textAlign: 'center', padding: '30px 0' }}>
        <LoadingOutlined style={{ fontSize: 36, color: '#722ed1' }} spin />
        <Title level={5} style={{ color: '#1a3353', margin: '16px 0 8px' }}>Submitting Revision...</Title>
        <div style={{ maxWidth: 300, margin: '20px auto 0' }}>
          <Steps direction="vertical" size="small" current={submitStep} items={[
            { title: 'Uploading document' },
            { title: 'Saving revision' },
            { title: 'Finalizing' },
            { title: 'Complete' },
          ]} />
        </div>
      </div>
    );
  }

  // ── DONE ──
  if (revStep === 'done') {
    return (
      <div style={{ textAlign: 'center', padding: '30px 0' }}>
        <CheckCircleOutlined style={{ fontSize: 40, color: '#52c41a', marginBottom: 12 }} />
        <Title level={5} style={{ color: '#1a3353', margin: '0 0 8px' }}>Revision Submitted Successfully</Title>
        <Text style={{ color: '#8c8c8c' }}>The revised invoice has been sent for re-review.</Text>
      </div>
    );
  }

  return null;
};

export default RevisionUploadForm;